import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  IndianRupee, TrendingUp, AlertCircle, Clock, Users, Building2, 
  FileText, PiggyBank, ArrowUpRight, ArrowDownRight, ReceiptText, TrendingDown, Landmark
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#002FA7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const COLOR_BG_MAP = {
  'text-primary': 'bg-primary/10',
  'text-emerald-600': 'bg-emerald-50',
  'text-amber-600': 'bg-amber-50',
  'text-red-600': 'bg-red-50',
};

function StatCard({ icon: Icon, label, value, subtext, color = 'text-primary', trend }) {
  const bgColor = COLOR_BG_MAP[color] || 'bg-muted';
  return (
    <Card className="p-5 border-border">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">{label}</p>
          <p className={`text-2xl font-heading font-bold ${color}`}>{value}</p>
          {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
        </div>
        <div className={`p-2 rounded-sm ${bgColor}`}>
          <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.5} />
        </div>
      </div>
      {trend && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}% from last month
        </div>
      )}
    </Card>
  );
}

export default function FinanceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(() => {
    api.get('/finance/dashboard')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load finance dashboard'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) return <div className="text-muted-foreground">Loading dashboard...</div>;
  if (!data) return <div className="text-muted-foreground">No data available</div>;

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN')}`;

  const statusData = Object.entries(data.invoice_status_counts || {}).map(([status, count]) => ({
    name: status.replace('_', ' ').toUpperCase(),
    value: count,
  })).filter(d => d.value > 0);

  return (
    <div data-testid="finance-dashboard">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Finance Dashboard</h2>
        <p className="text-sm text-muted-foreground mt-1">FY {data.financial_year} Overview</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={IndianRupee} label="Monthly Revenue" value={formatCurrency(data.monthly_revenue)} color="text-emerald-600" />
        <StatCard icon={Clock} label="Total Outstanding" value={formatCurrency(data.total_outstanding)} color="text-amber-600" />
        <StatCard icon={AlertCircle} label="Overdue Amount" value={formatCurrency(data.overdue_amount)} color="text-red-600" />
        <StatCard icon={PiggyBank} label="Incentive Liability" value={formatCurrency(data.incentive_liability)} color="text-violet-600" />
      </div>

      {/* TDS Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="tds-metrics-row">
        <Card className="p-5 border-border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-blue-700 uppercase tracking-widest mb-1">TDS Deducted (YTD)</p>
              <p className="text-2xl font-heading font-bold text-blue-700" data-testid="tds-ytd-value">{formatCurrency(data.tds?.ytd_amount || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Claimable on year-end tax filing</p>
            </div>
            <div className="p-2 rounded-sm bg-blue-100 dark:bg-blue-900">
              <Landmark className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="p-5 border-border">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">TDS This Month</p>
              <p className="text-2xl font-heading font-bold text-foreground">{formatCurrency(data.tds?.monthly_amount || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Tax deducted at source by clients</p>
            </div>
            <div className="p-2 rounded-sm bg-muted">
              <ReceiptText className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="p-5 border-border">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Net Cash Received (YTD)</p>
              <p className="text-2xl font-heading font-bold text-emerald-700">{formatCurrency(data.tds?.net_received_ytd || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Actual bank inflows from payments</p>
            </div>
            <div className="p-2 rounded-sm bg-emerald-50 dark:bg-emerald-900/30">
              <IndianRupee className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
            </div>
          </div>
        </Card>
      </div>

      {/* Credit Notes & Net Revenue Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 border-border bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-red-600 uppercase tracking-widest mb-1">Credit Notes (This Month)</p>
              <p className="text-2xl font-heading font-bold text-red-600">{formatCurrency(data.credit_notes?.monthly_amount || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">{data.credit_notes?.monthly_count || 0} credit note(s)</p>
            </div>
            <div className="p-2 rounded-sm bg-red-100 dark:bg-red-900">
              <ReceiptText className="w-5 h-5 text-red-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="p-5 border-border bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-emerald-600 uppercase tracking-widest mb-1">Net Revenue (This Month)</p>
              <p className="text-2xl font-heading font-bold text-emerald-600">{formatCurrency(data.net_monthly_revenue || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Revenue - Credit Notes</p>
            </div>
            <div className="p-2 rounded-sm bg-emerald-100 dark:bg-emerald-900">
              <TrendingUp className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="p-5 border-border">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Credit Notes (FY {data.financial_year})</p>
              <p className="text-2xl font-heading font-bold text-red-600">{formatCurrency(data.credit_notes?.total_amount_fy || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">{data.credit_notes?.count_fy || 0} total credit note(s)</p>
            </div>
            <div className="p-2 rounded-sm bg-red-50 dark:bg-red-900/30">
              <TrendingDown className="w-5 h-5 text-red-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>
      </div>

      {/* Revenue vs Payroll */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="p-5 border-border">
          <h3 className="font-heading font-semibold text-foreground mb-4">Revenue vs Payroll (This Month)</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Revenue</span>
              <span className="font-mono font-medium text-emerald-600">{formatCurrency(data.revenue_vs_payroll?.revenue)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Payroll Cost</span>
              <span className="font-mono font-medium text-red-600">{formatCurrency(data.revenue_vs_payroll?.payroll)}</span>
            </div>
            <div className="border-t border-border pt-2 flex justify-between items-center">
              <span className="text-sm font-medium text-foreground">Margin</span>
              <span className={`font-mono font-bold ${data.revenue_vs_payroll?.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(data.revenue_vs_payroll?.margin)}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-5 border-border">
          <h3 className="font-heading font-semibold text-foreground mb-4">Invoice Status</h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={60}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {statusData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No invoices yet</p>
          )}
          <div className="flex flex-wrap gap-2 justify-center mt-2">
            {statusData.map((entry, index) => (
              <Badge key={entry.name} style={{ backgroundColor: COLORS[index % COLORS.length] }} className="text-white text-xs">
                {entry.name}: {entry.value}
              </Badge>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-border">
          <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Top Clients by Revenue
          </h3>
          {data.top_clients?.length > 0 ? (
            <div className="space-y-2">
              {data.top_clients.map((client, i) => (
                <div key={client.client_id} className="flex justify-between items-center py-1.5 border-b border-border last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{i + 1}</span>
                    <span className="text-sm truncate max-w-[150px]">{client.client_name}</span>
                  </div>
                  <span className="font-mono text-sm font-medium">{formatCurrency(client.revenue)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No revenue data</p>
          )}
        </Card>
      </div>

      {/* Recruiter Revenue */}
      <Card className="p-5 border-border">
        <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
          <Users className="w-4 h-4" /> Revenue by Recruiter
        </h3>
        {data.revenue_per_recruiter?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.revenue_per_recruiter} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
              <YAxis dataKey="recruiter_name" type="category" width={120} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="revenue" fill="#002FA7" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">No recruiter revenue data</p>
        )}
      </Card>
    </div>
  );
}
